<template>
  <div class="conditional-renderer">
    <!-- Top Navigation Bar -->
    <topnavbar
      :testData="props.testData"
      :answers="{}"
      :stop-timer="props.stopTimer"
      @time-up="$emit('time-up')"
    />

    <div class="container wrapper mt-4" :style="containerStyles">
      <!-- Test Title (only show for non-PageNodes, PageNodeRenderer has its own title) -->
      <h1 v-if="props.testData && !props.isPageNode">{{ props.testData.title }}</h1>
      <p v-if="!props.isPageNode" class="test-description">{{ props.testData.Description }}</p>

      <!-- PageNode Renderer -->
      <div v-if="props.isPageNode && props.pageNode">
        <PageNodeRenderer
          :pageNode="props.pageNode"
          :childNodes="props.pageChildNodes"
          :childElements="props.pageChildElements"
          :testData="props.testData"
          :onContinue="handlePageContinue"
          :stopTimer="props.stopTimer"
          :previewMode="props.previewMode"
          @time-up="$emit('time-up')"
        />
      </div>

      <!-- Current Interaction (for non-PageNodes) -->
      <div v-else-if="props.currentElement" class="interaction-container">
        <div class="card shadow-sm" :style="cardStyles">
          <div class="card-body">
            <!-- Render the interaction component dynamically -->
            <component
              :is="interactionComponent.component"
              v-bind="interactionComponent.props"
              @interaction-started="handleInteractionStarted"
              @interaction-completed="handleInteractionCompleted"
            />
          </div>
        </div>

        <!-- Submit Button -->
        <div class="submit-section" :style="submitButtonStyles">
          <button
            class="btn btn-primary btn-lg"
            @click="handleSubmit"
            :disabled="!isAnswered"
            data-conditional-submit="submitAnswer"
          >
            {{ isAnswered ? 'Soumettre la réponse →' : 'Veuillez répondre à la question' }}
          </button>
        </div>
      </div>

      <!-- No Element State -->
      <div v-else class="alert alert-warning">
        ⚠️ Aucune interaction à afficher
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, watch, onMounted, ref } from 'vue';
import { useResponsesStore } from '@/stores/useResponsesStore';
import topnavbar from './topnavbar.vue';

// État local pour tracker si l'interaction est complétée (pour le mode preview)
const localInteractionCompleted = ref(false);

// Helper function to decode JWT token
function decodeToken(token) {
  if (!token) {
    console.error('Le token est null ou non défini');
    return null;
  }
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (error) {
    console.error('Erreur lors du décodage du token :', error);
    return null;
  }
}

// Import interaction renders
import ChoiceRender from './interactions/ChoiceRender.vue';
import ChoiceBlockRender from './interactions/ChoiceBlockRender.vue';
import TextGapsRender from './interactions/TextGapsRender.vue';
import ShortAnswerRender from './interactions/ShortAnswerRender.vue';
import OrderRender from './interactions/OrderRender.vue';
import MatchRender from './interactions/MatchRender.vue';
import MakingPairsRender from './interactions/MakingPairsRender.vue';
import HotSpotRender from './interactions/hotSpotRender.vue';
import MessageRender from './interactions/MessageRender.vue';
import TextPuzzleRender from './interactions/TextPuzzleRender.vue';
import PageNodeRenderer from './PageNodeRenderer.vue';

const responsesStore = useResponsesStore();

const props = defineProps({
  currentElement: {
    type: Object,
    default: null // Changed to optional for PageNodes
  },
  testData: {
    type: Object,
    required: true
  },
  currentNodeData: {
    type: Object,
    default: null
  },
  onSubmitAnswer: {
    type: Function,
    required: true
  },
  stopTimer: {
    type: Boolean,
    default: false
  },
  previewMode: {
    type: Boolean,
    default: false
  },
  // New props for PageNode support
  isPageNode: {
    type: Boolean,
    default: false
  },
  pageNode: {
    type: Object,
    default: null
  },
  pageChildNodes: {
    type: Array,
    default: () => []
  },
  pageChildElements: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['time-up']);

/**
 * Map interaction types to their render components
 */
const interactionComponentsMap = {
  choice: ChoiceRender,
  choiceBlock: ChoiceBlockRender,
  textGaps: TextGapsRender,
  shortAnswer: ShortAnswerRender,
  order: OrderRender,
  match: MatchRender,
  makingPairs: MakingPairsRender,
  hotSpot: HotSpotRender,
  message: MessageRender,
  textPuzzle: TextPuzzleRender
};

/**
 * Prepare the interaction component and its props
 */
const interactionComponent = computed(() => {
  const element = props.currentElement;
  const interactionType = element.el_Type;

  const component = interactionComponentsMap[interactionType];

  if (!component) {
    console.error('❌ Unknown interaction type:', interactionType);
    return {
      component: 'div',
      props: {}
    };
  }

  // Build props for the component
  const componentProps = {
    question: element, // Most render components expect 'question' prop
    testData: props.testData,
    questionIndex: 1, // Always 1 for conditional (single question display)
    questionIndexMap: {}, // Empty map for conditional tests (no page-based indexing)
    readOnly: false
  };

  return {
    component,
    props: componentProps
  };
});

/**
 * Container styles based on display settings
 */
const containerStyles = computed(() => {
  const settings = props.testData?.displaySettings;
  console.log('🎨 [ConditionalRenderer] containerStyles computed - settings:', settings);
  console.log('🎨 [ConditionalRenderer] props.testData:', props.testData);

  if (!settings) {
    console.warn('⚠️ [ConditionalRenderer] No displaySettings found in testData');
    return {};
  }

  const styles = {
    margin: `${settings.containerMargin || 20}px auto`,
    padding: `${settings.containerPadding || 20}px`,
    backgroundColor: settings.containerBackground || '#ffffff',
    maxWidth: settings.maxWidth && settings.maxWidth > 0 ? `${settings.maxWidth}px` : '100%',
    marginLeft: settings.containerAlignment === 'left' ? '0' :
                settings.containerAlignment === 'right' ? 'auto' : 'auto',
    marginRight: settings.containerAlignment === 'right' ? '0' :
                 settings.containerAlignment === 'left' ? 'auto' : 'auto',
    '--question-spacing': `${settings.questionSpacing || 15}px`,
    color: settings.textColor || '#000000'
  };

  console.log('🎨 [ConditionalRenderer] Applied styles:', styles);
  return styles;
});

/**
 * Submit button styles based on display settings
 */
const submitButtonStyles = computed(() => {
  const settings = props.testData?.displaySettings;

  if (!settings) {
    return {
      marginTop: '50px',
      marginBottom: '20px',
      textAlign: 'center'
    };
  }

  return {
    marginTop: `${settings.submitButtonMarginTop || 20}px`,
    marginBottom: `${settings.submitButtonMarginBottom || 20}px`,
    textAlign: settings.submitButtonAlignment || 'center'
  };
});

/**
 * Card styles based on display settings (for pageBackground)
 */
const cardStyles = computed(() => {
  const settings = props.testData?.displaySettings;

  if (!settings) {
    return {};
  }

  return {
    backgroundColor: settings.pageBackground || '#f8f9fa',
    padding: `${settings.pagePadding || 15}px`,
    margin: '0',
    boxSizing: 'border-box',
    width: '100%'
  };
});

/**
 * Check if the current element has been answered
 * Utilise soit le store (mode normal) soit l'état local (mode preview)
 */
const isAnswered = computed(() => {
  // Si l'état local indique que c'est complété, activer le bouton
  if (localInteractionCompleted.value) {
    return true;
  }

  // Sinon, vérifier dans le store (mode normal)
  const response = responsesStore.responses.find(
    r => r.questionId === props.currentElement.el_ID
  );

  if (!response) return false;

  // Check if answered based on interaction type
  if (response.type === 'choice' || response.type === 'choiceBlock') {
    return response.selectedOptions && response.selectedOptions.length > 0;
  }

  if (response.type === 'textGaps') {
    return response.answered === true;
  }

  if (response.type === 'shortAnswer') {
    return response.selectedOptions && response.selectedOptions.trim() !== '';
  }

  if (response.type === 'order') {
    return response.labelsOrder && response.labelsOrder.length > 0 && response.answered === true;
  }

  if (response.type === 'match') {
    return response.answered === true;
  }

  if (response.type === 'makingPairs') {
    return response.selectedPairs && response.selectedPairs.length > 0;
  }

  if (response.type === 'hotSpot') {
    return response.placedStickersData && response.placedStickersData.length > 0;
  }

  if (response.type === 'textPuzzle') {
    return response.gapAnswers && Object.keys(response.gapAnswers).length > 0;
  }

  if (response.type === 'message') {
    return true; // Messages are always "answered"
  }

  return response.answered === true;
});

/**
 * Handle submit button click
 */
const handleSubmit = () => {
  if (!isAnswered.value) {
    console.warn('⚠️ Not answered yet');
    return;
  }

  // Chercher la réponse dans le store
  let response = responsesStore.responses.find(
    r => r.questionId === props.currentElement.el_ID
  );

  // Si pas de réponse dans le store (mode preview avec localInteractionCompleted)
  // créer une réponse temporaire minimale
  if (!response && localInteractionCompleted.value) {
    console.log('🎬 [Preview Mode] Création réponse temporaire pour:', props.currentElement.el_ID);
    response = {
      questionId: props.currentElement.el_ID,
      type: props.currentElement.el_Type,
      answered: true,
      selectedOptions: [], // Vide car mode preview
      score: 0,
      maxScore: 0,
      questionLabel: props.currentElement.el_Label || props.currentElement.el_Text
    };
  }

  if (!response) {
    console.warn('⚠️ No response found');
    return;
  }

  console.log('📤 Submitting answer:', response);

  // Call the parent handler
  props.onSubmitAnswer(response);
};

/**
 * Handle interaction started event (for timing, etc.)
 */
const handleInteractionStarted = (data) => {
  console.log('🎯 Interaction started:', data);
};

/**
 * Handle interaction completed event
 * Utilisé pour tracker la complétion sans sauvegarder dans le store
 */
const handleInteractionCompleted = (data) => {
  console.log('✅ [ConditionalRenderer] Interaction completed:', data);
  localInteractionCompleted.value = true;
};

/**
 * Handle PageNode continue button click
 * @param {Array} childResponses - Array of { nodeId, response } for all child interactions
 */
const handlePageContinue = (childResponses) => {
  console.log('📄 PageNode continue clicked with responses:', childResponses);

  // Call parent with a special PageNode response that includes all child responses
  // This will be handled by ConditionalTestRunner to navigate to next node
  props.onSubmitAnswer({
    isPageNode: true,
    pageNodeId: props.pageNode.id,
    childResponses: childResponses
  });
};

/**
 * Auto-submit pour les messages (après 1 seconde d'affichage)
 */
onMounted(() => {
  if (props.currentElement.el_Type === 'message') {
    console.log('💬 Message détecté - auto-submit dans 2 secondes');

    // Créer une fausse réponse pour les messages
    const token = localStorage.getItem('token');
    const decodedToken = decodeToken(token);
    const userId = decodedToken?._id || decodedToken?.sub;

    responsesStore.saveResponse({
      userId: userId,
      questionId: props.currentElement.el_ID,
      type: 'message',
      answered: true,
      selectedOptions: 'message-viewed',
      score: 0,
      maxScore: 0,
      questionLabel: props.currentElement.el_Label || 'Message'
    });

    // Auto-submit après 2 secondes
    setTimeout(() => {
      handleSubmit();
    }, 2000);
  }
});

/**
 * Watch pour gérer le changement d'élément (si nouveau message)
 */
watch(() => props.currentElement, (newElement, oldElement) => {
  console.log('🔄 [ConditionalRenderer] Changement d\'élément:', {
    old: oldElement?.el_ID,
    oldLabel: oldElement?.el_Label,
    new: newElement?.el_ID,
    newLabel: newElement?.el_Label,
    sameElement: oldElement?.el_ID === newElement?.el_ID
  });

  // Réinitialiser l'état local quand on change d'élément
  localInteractionCompleted.value = false;

  // En mode preview, nettoyer le store pour éviter la restauration de l'ancien état
  if (props.previewMode && oldElement) {
    console.log('🎬 [Preview Mode] Nettoyage du store pour l\'ancien élément:', oldElement.el_ID);
    // Supprimer l'ancienne réponse du store
    const index = responsesStore.responses.findIndex(r => r.questionId === oldElement.el_ID);
    if (index !== -1) {
      responsesStore.responses.splice(index, 1);
      console.log('🗑️ [Preview Mode] Réponse supprimée du store');
    }
  }

  if (newElement && newElement.el_Type === 'message') {
    console.log('💬 Nouveau message détecté - auto-submit dans 2 secondes');

    const token = localStorage.getItem('token');
    const decodedToken = decodeToken(token);
    const userId = decodedToken?._id || decodedToken?.sub;

    responsesStore.saveResponse({
      userId: userId,
      questionId: newElement.el_ID,
      type: 'message',
      answered: true,
      selectedOptions: 'message-viewed',
      score: 0,
      maxScore: 0,
      questionLabel: newElement.el_Label || 'Message'
    });

    setTimeout(() => {
      handleSubmit();
    }, 2000);
  }
});
</script>

<style scoped>
.conditional-renderer {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.wrapper {
  max-width: 900px;
  margin: 0 auto;
  padding: 50px;
}

.test-description {
  color: #6c757d;
  margin-bottom: 30px;
}

.interaction-container {
  margin-top: 30px;
}

.card {
  border: none;
  border-radius: 10px;
}

.submit-section {
  text-align: center;
}

.btn-primary {
  padding: 12px 40px;
  font-size: 1.1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
